import { z } from 'zod';
import { PointSchema } from './geojson';

/**
 * Camera smoke detection feature (Phase E.W.5) — a PRE-FUSION camera-CV smoke
 * verdict served by GET /api/camera-smoke. Backed directly by the E.W.2
 * `camera_smoke_detections` table (the Sonnet confirm-stage rows), anchored to a
 * CAMERA's location.
 *
 * This is the camera-CV SIBLING of the satellite Raw Hot Spots (raw-hotspot.ts):
 * a detection tells you "a camera saw smoke here", not "the fusion engine scored
 * this as a fire". Camera smoke detections are an AMBIENT map layer
 * (operator-cop-doctrine §3B.8) — NEVER the actionable smoke_detected Anomaly-
 * Queue dot (that act-register surface is untouched) — and feed the F.1 fusion
 * `cameraSmoke` corroborating signal.
 *
 * Two shapes: the LIST endpoint returns the common feature; the DETAIL endpoint
 * (`/:id`) adds the in-frame bbox, PTZ aim, model/prompt provenance, the camera
 * links, and a co-located F.1 fused-hotspot link (within 1 km), so the operator
 * can jump from the raw camera signal to its actionable scored peer.
 */

export const CameraSmokePropertiesSchema = z.object({
  id: z.string().uuid(),
  /** The camera this detection was made on (the anchor + the camera-link target). */
  camera_id: z.string().uuid(),
  /** Human camera name (colour/source is never load-bearing alone). */
  camera_name: z.string(),
  /** Upstream camera source slug (e.g. 'alertwest', 'ontario-511'). */
  camera_source: z.string().nullable(),
  /** Sonnet confirm-stage confidence (0–100), when reported. */
  confidence: z.number().nullable(),
  /** When WE ran the inference (UTC ISO) — drives the freshness / "ago" read. */
  observed_at: z.string(),
  /** Upstream frame capture time (UTC ISO), distinct from observed_at. */
  frame_captured_at: z.string().nullable(),
  /** Confirm-stage model id (e.g. 'claude-sonnet-4-6'). */
  model: z.string(),
});
export type CameraSmokeProperties = z.infer<typeof CameraSmokePropertiesSchema>;

export const CameraSmokeFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: CameraSmokePropertiesSchema,
});
export type CameraSmokeFeature = z.infer<typeof CameraSmokeFeatureSchema>;

export const CameraSmokeFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(CameraSmokeFeatureSchema),
});
export type CameraSmokeFeatureCollection = z.infer<typeof CameraSmokeFeatureCollectionSchema>;

/** The detail endpoint adds the bbox + provenance + camera links + the
 *  co-located fused-hotspot link. */
export const CameraSmokeDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: CameraSmokePropertiesSchema.extend({
    /** [x, y, w, h] normalized in-frame bounding box of the smoke, when given. */
    bbox: z.array(z.number()).nullable(),
    /** PTZ aim azimuth at capture (ALERTWest); NULL for fixed cams. */
    ptz_azimuth_deg: z.number().nullable(),
    /** Inference stage — always 'confirm' for this rendered layer. */
    stage: z.string(),
    /** Prompt version that produced the verdict (model/prompt provenance). */
    prompt_version: z.string().nullable(),
    /** Model's free-text reasoning, when captured (audit provenance). */
    reasoning: z.string().nullable(),
    /** The camera's live snapshot URL (the "bbox thumbnail" source, when stored). */
    camera_snapshot_url: z.string().nullable(),
    /** The camera's operator viewer URL (deep-link to the camera). */
    camera_viewer_url: z.string().nullable(),
    /** The camera's stream type (mjpeg / hls / youtube_live / …). */
    camera_stream_type: z.string().nullable(),
    camera_province: z.string().nullable(),
    /** The nearest LIVE F.1-scored high_risk_hotspot within 1 km, if any — lets
     *  the operator open the actionable scored sibling of this camera signal. */
    fused_hotspot_nearby: z
      .object({
        id: z.string().uuid(),
        confidence: z.number(),
        response_level: z.number(),
        distance_m: z.number(),
      })
      .nullable(),
  }),
});
export type CameraSmokeDetail = z.infer<typeof CameraSmokeDetailSchema>;
